import { recipeApp, type OsRecipe } from '../os-recipes.js';

const LINEAR = recipeApp('com.linear');

/** linear: its app recipes as data (Mac recipes R3). Every entry ships held until its drill lifts it.
 *  Linear is Electron and navigates on bare letters, so every bare key runs behind the focus guard.
 *  The research rows not here are on the R3 review lists. */
export const RECIPES: readonly OsRecipe[] = [
  {
    id: 'linear.new_issue', app: LINEAR, status: 'held', opens: true,
    title: "Open Linear's new issue form",
    description: 'Open Linear\'s new issue form ("new issue in linear").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+n' }],
    tier: 'reversible', cues: ['naya', 'new issue', 'new ticket', 'new linear ticket', 'new bug', 'create an issue', 'create a ticket', 'naya issue'],
    reply: 'The new issue form is open in Linear.',
  },
  // Creating an issue is outward (the team is notified): the form waits, and Cmd+Return creates it on the yes.
  {
    id: 'linear.draft_issue', app: LINEAR, status: 'held',
    title: 'Draft a Linear issue with a title',
    description: 'Draft a Linear issue with a title ("create an issue called fix the login redirect").',
    params: { title: { kind: 'literal', description: 'The issue title, their exact words', cut: ['after_saying', 'called'] } },
    steps: [{ op: 'open_url', template: 'linear://new?title={title}' }, { op: 'await_front', timeoutMs: 3000 }, { op: 'settle', ms: 500 }, { op: 'memo' }],
    onYes: [{ op: 'send_key', chord: 'cmd+return' }],
    tier: 'unsent', cues: ['issue', 'ticket', 'bug', 'file a bug', 'log a bug', 'issue banao'],
    reply: 'The issue {title} is drafted in Linear. Create it?',
  },
  {
    id: 'linear.draft_issue_in_team', app: LINEAR, status: 'held',
    title: 'Draft a Linear issue in a named team',
    description: 'Draft a Linear issue in a named team ("create a design team issue called new icons").',
    params: { team: { kind: 'span', description: "The team's key or name as they said it" }, title: { kind: 'literal', description: 'The issue title, their exact words', cut: ['after_saying', 'called'] } },
    steps: [{ op: 'open_url', template: 'linear://team/{team}/new?title={title}' }, { op: 'await_front', timeoutMs: 3000 }, { op: 'settle', ms: 500 }, { op: 'memo' }],
    onYes: [{ op: 'send_key', chord: 'cmd+return' }],
    tier: 'unsent', cues: ['issue', 'ticket', 'bug', 'liye issue banao'],
    reply: 'The issue {title} is drafted for the {team} team in Linear. Create it?',
  },
  {
    id: 'linear.new_project', app: LINEAR, status: 'held', opens: true,
    title: "Open Linear's new project form",
    description: 'Open Linear\'s new project form ("new project in linear").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['File', 'New Project…'] }],
    tier: 'reversible', cues: ['naya', 'new project', 'create a project', 'create a linear project', 'new project form', 'naya project'],
    reply: 'The new project form is open in Linear.',
  },
  // The guard sits right before the bare "/" (a mark between them would drop it).
  // drill: Electron focus role
  {
    id: 'linear.search', app: LINEAR, status: 'held', opens: true,
    title: 'Search Linear',
    description: 'Search Linear ("search linear for login bug").',
    params: { query: { kind: 'literal', description: 'The exact words to search for', cut: 'query' } },
    steps: [{ op: 'activate' }, { op: 'focus_mark' }, { op: 'focus_not_editable' }, { op: 'key', chord: '/' }, { op: 'focus_moved', timeoutMs: 800, labelIn: ['search'] }, { op: 'type', param: 'query' }],
    tier: 'reversible', cues: ['search', 'find', 'look', 'dhundo'],
    reply: 'Searching Linear for {query}.',
  },
  // drill: Electron focus role
  {
    id: 'linear.my_issues', app: LINEAR, status: 'held', opens: true,
    title: 'Open My Issues in Linear',
    description: 'Open My Issues in Linear ("show my issues").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'g' }, { op: 'key', chord: 'm' }],
    tier: 'reversible', cues: ['my issues', 'assigned', 'tickets', 'issues', 'meri issues dikhao'],
    reply: 'Opened My Issues in Linear.',
  },
  // drill: Electron focus role
  {
    id: 'linear.active_issues', app: LINEAR, status: 'held', opens: true,
    title: "Open the team's active issues",
    description: 'Open the team\'s active issues ("show active issues").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'g' }, { op: 'key', chord: 'a' }],
    tier: 'reversible', cues: ['active', 'progress', 'active issues', 'active view', 'active issues dikhao', 'team s active tickets'],
    reply: 'Opened active issues in Linear.',
  },
  // drill: Electron focus role
  {
    id: 'linear.backlog', app: LINEAR, status: 'held', opens: true,
    title: 'Open the team backlog',
    description: 'Open the team backlog ("open the backlog").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'g' }, { op: 'key', chord: 'b' }],
    tier: 'reversible', cues: ['backlog', 'backlog dikhao'],
    reply: 'Opened the backlog in Linear.',
  },
  // drill: Electron focus role
  {
    id: 'linear.current_cycle', app: LINEAR, status: 'held', opens: true,
    title: 'Open the current cycle',
    description: 'Open the current cycle ("open the current cycle").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'g' }, { op: 'key', chord: 'v' }],
    tier: 'reversible', cues: ['cycle', 'sprint', 'current cycle', 'this cycle', 'current sprint dikhao', 'this week s cycle'],
    reply: 'Opened the current cycle in Linear.',
  },
  // drill: Electron focus role
  {
    id: 'linear.triage', app: LINEAR, status: 'held', opens: true,
    title: 'Open Linear triage',
    description: 'Open Linear triage ("open triage").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'g' }, { op: 'key', chord: 't' }],
    tier: 'reversible', cues: ['triage', 'triage queue'],
    reply: 'Opened triage in Linear.',
  },
  // drill: Electron focus role
  {
    id: 'linear.archive', app: LINEAR, status: 'held', opens: true,
    title: 'Open the Linear archive',
    description: 'Open the Linear archive ("open the archive").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'g' }, { op: 'key', chord: 'x' }],
    tier: 'reversible', cues: ['archive', 'archived', 'archives', 'deleted', 'archived issues', 'archive dikhao', 'deleted linear issue'],
    reply: 'Opened the archive in Linear.',
  },
  // drill: Electron focus role
  {
    id: 'linear.projects', app: LINEAR, status: 'held', opens: true,
    title: 'Open Linear projects',
    description: 'Open Linear projects ("show projects").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'g' }, { op: 'key', chord: 'p' }],
    tier: 'reversible', cues: ['projects', 'project', 'projects list', 'all linear projects', 'projects dikhao'],
    reply: 'Opened projects in Linear.',
  },
  // No bare "start" cue: "start Linear" is a launch, never Home.
  {
    id: 'linear.home', app: LINEAR, status: 'held', opens: true,
    title: 'Go to Linear home',
    description: 'Go to Linear home ("go home in linear").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+shift+h' }],
    tier: 'reversible', cues: ['home', 'start page', 'go home in', 'ka home', 'open linear home please'],
    reply: 'Went home in Linear.',
  },
  // Teammates see an assignment and subscribers are notified: the bare i waits for the yes.
  // drill: Electron focus role
  {
    id: 'linear.assign_to_me', app: LINEAR, status: 'held',
    title: 'Assign the Linear issue to me',
    description: 'Assign the Linear issue to me ("assign this to me").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'memo' }],
    onYes: [{ op: 'focus_not_editable' }, { op: 'key', chord: 'i' }],
    tier: 'irreversible', cues: ['assign', 'assignee', 'myself', 'mujhe', 'assign this', 'assign this ticket', 'assign it', 'yeh issue mujhe'],
    reply: 'Assign this issue to you in Linear?',
  },
  // A status change notifies the team (Done can close linked work): the picker, the typed status and its Return all wait for the yes.
  // drill: Electron focus role
  {
    id: 'linear.set_status', app: LINEAR, status: 'held',
    title: "Set the Linear issue's status",
    description: 'Set the Linear issue\'s status ("mark this as done").',
    params: { status: { kind: 'enum', description: 'The status to set', enum: ['Backlog', 'Todo', 'In Progress', 'In Review', 'Done', 'Canceled'] } },
    steps: [{ op: 'activate' }, { op: 'focus_not_editable' }, { op: 'memo' }],
    onYes: [{ op: 'focus_mark' }, { op: 'focus_not_editable' }, { op: 'key', chord: 's' }, { op: 'focus_moved', timeoutMs: 800, labelIn: ['change status', 'status', 'filter'] }, { op: 'type', param: 'status' }, { op: 'settle', ms: 300 }, { op: 'key', chord: 'return' }],
    tier: 'irreversible', yesWord: 'move', cues: ['status', 'done', 'progress', 'todo', 'review', 'cancel', 'canceled', 'mark this as done', 'set status', 'isko done mark'],
    reply: 'Move this issue to {status} in Linear?',
  },
  {
    id: 'linear.copy_issue_url', app: LINEAR, status: 'held',
    title: "Copy the Linear issue's link",
    description: 'Copy the Linear issue\'s link ("copy the issue link").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+shift+,' }, { op: 'verb', id: 'system.clipboard_read', params: {} }],
    tier: 'reversible', verify: 'echo', cues: ['copy', 'link', 'url', 'issue link', 'copy this ticket s', 'link to issue', 'issue ka link'],
    reply: 'Copied the issue link.',
  },
  {
    id: 'linear.copy_issue_id', app: LINEAR, status: 'held',
    title: "Copy the Linear issue's id",
    description: 'Copy the Linear issue\'s id ("copy the issue id").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+.' }, { op: 'verb', id: 'system.clipboard_read', params: {} }],
    tier: 'reversible', verify: 'echo', cues: ['copy', 'id', 'number', 'key', 'issue id', 'ticket number', 'grab the issue key'],
    reply: 'Copied the issue id.',
  },
  {
    id: 'linear.settings', app: LINEAR, status: 'held', opens: true,
    title: 'Open Linear preferences',
    description: 'Open Linear preferences ("open linear settings").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+,' }],
    tier: 'reversible', cues: ['settings', 'preferences', 'change my linear preferences'],
    reply: 'Opened Linear settings.',
  },
  {
    id: 'linear.new_window', app: LINEAR, status: 'held', opens: true,
    title: 'Open a new Linear window',
    description: 'Open a new Linear window ("new linear window").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+shift+n' }],
    tier: 'reversible', cues: ['nayi', 'another', 'second', 'new window', 'new linear window', 'another window', 'another linear window', 'second window', 'nayi window'],
    reply: 'Opened a new Linear window.',
  },
  // A close is a destructive menu word to the lint, so it waits for the yes.
  {
    id: 'linear.close_tab', app: LINEAR, status: 'held',
    title: 'Close the Linear tab',
    description: 'Close the Linear tab ("close this tab in linear").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'memo' }],
    onYes: [{ op: 'menu', path: ['File', 'Close Tab'] }],
    tier: 'irreversible', cues: ['close this tab', 'close the linear tab', 'shut this tab', 'close tab', 'tab band'],
    reply: 'Close this tab in Linear?',
  },
  {
    id: 'linear.reload', app: LINEAR, status: 'held',
    title: 'Reload Linear',
    description: 'Reload Linear ("reload linear").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+r' }],
    tier: 'reversible', cues: ['reload', 'refresh', 'is stuck refresh it', 'reload the page'],
    reply: 'Reloaded Linear.',
  },
  {
    id: 'linear.zoom_in', app: LINEAR, status: 'held',
    title: 'Zoom in on Linear',
    description: 'Zoom in on Linear ("zoom in on linear").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+=' }],
    tier: 'reversible', cues: ['zoom', 'bigger', 'larger', 'bada', 'zoom in on', 'bigger text', 'zoom in'],
    reply: 'Zoomed in.',
  },
  {
    id: 'linear.zoom_out', app: LINEAR, status: 'held',
    title: 'Zoom out on Linear',
    description: 'Zoom out on Linear ("zoom out on linear").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+-' }],
    tier: 'reversible', cues: ['zoom', 'smaller', 'shrink', 'chhota', 'zoom out on', 'zoom out', 'zoom out a little'],
    reply: 'Zoomed out.',
  },
  {
    id: 'linear.zoom_reset', app: LINEAR, status: 'held',
    title: 'Reset Linear zoom',
    description: 'Reset Linear zoom ("reset zoom in linear").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+0' }],
    tier: 'reversible', cues: ['zoom', 'actual', 'normal', 'reset', 'zoom in', 'actual size', 'back to normal size', 'undo the zoom in', 'normal size', 'the zoom in'],
    reply: 'Reset the zoom.',
  },
  {
    id: 'linear.full_screen', app: LINEAR, status: 'held',
    title: 'Toggle Linear full screen',
    description: 'Toggle Linear full screen ("full screen linear").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['View', 'Enter Full Screen'] }],
    tier: 'reversible', cues: ['full screen', 'fullscreen', 'exit full screen', 'toggle full screen'],
    reply: 'Toggled full screen in Linear.',
  },
  {
    id: 'linear.shortcuts_help', app: LINEAR, status: 'held', opens: true,
    title: "Open Linear's keyboard shortcuts guide",
    description: 'Open Linear\'s keyboard shortcuts guide ("show linear shortcuts").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['Help', 'Keyboard Shortcuts Guide'] }],
    tier: 'reversible', cues: ['shortcuts', 'hotkeys', 'shortcut', 'show linear shortcuts', 'shortcuts dikhao', 'keyboard shortcuts in', 'help with linear shortcuts'],
    reply: "Opened Linear's shortcuts guide.",
  },  // ── R3c: the catalog rulings' accepts ─────────────────────────────────────────────────────────
  // History through the menu bar: a menu press never reaches the editor, where cmd+[ and cmd+] may outdent and indent.
  {
    id: 'linear.back', app: LINEAR, status: 'held', opens: true,
    title: 'Go back in Linear',
    description: 'Go back in Linear ("go back in linear").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['History', 'Back'] }],
    tier: 'reversible', cues: ['back', 'previous', 'peeche', 'back a page', 'previous page', 'back one', 'peeche jao'],
    reply: 'Went back in Linear.',
  },
  {
    id: 'linear.forward', app: LINEAR, status: 'held', opens: true,
    title: 'Go forward in Linear',
    description: 'Go forward in Linear ("go forward in linear").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['History', 'Forward'] }],
    tier: 'reversible', cues: ['forward', 'aage', 'forward a page', 'forward one page', 'forward again', 'aage jao'],
    reply: 'Went forward in Linear.',
  },
];
